import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CATEGORIES, PLATFORMS, PLATFORM_LABELS } from '@affily/shared/constants';
import { formatCount, formatCents, formatPercent, bpsToPercent } from '@affily/shared/money';
import { useFetch } from '../hooks/useFetch.js';
import { qs } from '../lib/api.js';
import { Seo } from '../components/Seo.jsx';
import { TransparencyBadge, SelfReported } from '../components/Transparency.jsx';
import { FilterPanel, FilterGroup, FilterChip } from '../components/Filters.jsx';
import { Badge, Spinner, EmptyState, Button, Unknown } from '../components/ui.jsx';

const SORTS = [
  { value: 'transparency', label: 'Most transparent' },
  { value: 'followers', label: 'Largest audience' },
  { value: 'rate', label: 'Lowest starting rate' },
  { value: 'newest', label: 'Newest' },
];

const AUDIENCE_BANDS = [
  { label: 'Any', min: '', max: '' },
  { label: 'Nano · <10k', min: '', max: '10000' },
  { label: 'Micro · 10k–100k', min: '10000', max: '100000' },
  { label: 'Mid · 100k–1M', min: '100000', max: '1000000' },
  { label: 'Macro · 1M+', min: '1000000', max: '' },
];

const COUNTRIES = ['US', 'GB', 'CA', 'AU', 'DE', 'FR', 'BR', 'IN'];

export default function CreatorsDirectory() {
  const [params, setParams] = useSearchParams();

  const query = useMemo(
    () => ({
      q: params.get('q') ?? '',
      niche: params.getAll('niche'),
      platform: params.getAll('platform'),
      country: params.getAll('country'),
      minFollowers: params.get('minFollowers') ?? '',
      maxFollowers: params.get('maxFollowers') ?? '',
      maxRateCents: params.get('maxRateCents') ?? '',
      sort: params.get('sort') ?? 'transparency',
      page: params.get('page') ?? '1',
    }),
    [params],
  );

  const { data, loading } = useFetch(`/creators${qs(query)}`);

  function toggle(key, value) {
    const next = new URLSearchParams(params);
    const current = next.getAll(key);
    next.delete(key);
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updated.forEach((v) => next.append(key, v));
    next.delete('page');
    setParams(next);
  }

  function setValue(key, value) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  }

  function setBand(band) {
    const next = new URLSearchParams(params);
    band.min ? next.set('minFollowers', band.min) : next.delete('minFollowers');
    band.max ? next.set('maxFollowers', band.max) : next.delete('maxFollowers');
    next.delete('page');
    setParams(next);
  }

  const creators = data?.creators ?? [];
  const pagination = data?.pagination;
  const activeFilters =
    query.niche.length +
    query.platform.length +
    query.country.length +
    (query.minFollowers || query.maxFollowers ? 1 : 0) +
    (query.maxRateCents ? 1 : 0);

  return (
    <>
      <Seo
        title="Find UGC creators and influencers with public rates"
        description="Browse creators with public starting rates and verified or clearly labelled audience stats. Filter by niche, platform, audience size and country."
      />

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <header className="max-w-2xl">
          <h1 className="text-4xl font-semibold tracking-tight">Creators</h1>
          <p className="mt-2 text-ink-muted">
            Rates are public. Followers from a connected account are verified and dated; anything a
            creator typed in is labelled self-reported, with a link out so you can check it.
          </p>
        </header>

        <div className="mt-8 flex flex-col gap-8 lg:flex-row">
          <FilterPanel
            activeCount={activeFilters}
            onClear={() => setParams(new URLSearchParams(query.sort ? { sort: query.sort } : {}))}
            searchValue={query.q}
            onSearch={(v) => setValue('q', v)}
            searchPlaceholder="Search creators"
          >
            <FilterGroup label="Audience size">
              {AUDIENCE_BANDS.map((band) => (
                <FilterChip
                  key={band.label}
                  active={query.minFollowers === band.min && query.maxFollowers === band.max}
                  onClick={() => setBand(band)}
                >
                  {band.label}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup label="Budget per deliverable">
              {[
                { label: 'Any', value: '' },
                { label: 'Under $250', value: '25000' },
                { label: 'Under $1k', value: '100000' },
                { label: 'Under $5k', value: '500000' },
              ].map((opt) => (
                <FilterChip
                  key={opt.label}
                  active={query.maxRateCents === opt.value}
                  onClick={() => setValue('maxRateCents', opt.value)}
                >
                  {opt.label}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup label="Platform">
              {PLATFORMS.slice(0, 8).map((p) => (
                <FilterChip
                  key={p}
                  active={query.platform.includes(p)}
                  onClick={() => toggle('platform', p)}
                >
                  {PLATFORM_LABELS[p]}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup label="Audience country">
              {COUNTRIES.map((c) => (
                <FilterChip
                  key={c}
                  active={query.country.includes(c)}
                  onClick={() => toggle('country', c)}
                >
                  {c}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup label="Niche">
              {CATEGORIES.map((n) => (
                <FilterChip
                  key={n}
                  active={query.niche.includes(n)}
                  onClick={() => toggle('niche', n)}
                  className="capitalize"
                >
                  {n}
                </FilterChip>
              ))}
            </FilterGroup>
          </FilterPanel>

          <main className="min-w-0 flex-1">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-ink-muted">
                {loading ? 'Loading…' : `${formatCount(pagination?.total ?? 0)} creators`}
              </p>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-ink-muted">Sort</span>
                <select
                  value={query.sort}
                  onChange={(e) => setValue('sort', e.target.value)}
                  className="rounded-md border border-rule-strong bg-paper-raised px-2 py-1 text-sm"
                >
                  {SORTS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {query.sort === 'transparency' && (
              <p className="mb-3 rounded-md border border-accent/20 bg-accent-soft px-3 py-2 text-xs text-accent-ink">
                Default order is by transparency score — how much checkable information a creator
                has published. It is not a performance ranking and it cannot be bought.
              </p>
            )}

            {loading ? (
              <div className="flex justify-center py-16 text-ink-faint">
                <Spinner />
              </div>
            ) : creators.length === 0 ? (
              <EmptyState title="No creators match those filters">
                Try a wider audience band or budget.
              </EmptyState>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Creator</th>
                      <th>Platforms</th>
                      <th className="num">
                        Followers <SelfReported className="ml-1 normal-case" />
                      </th>
                      <th className="num">Engagement</th>
                      <th className="num">Rates from</th>
                      <th className="num">Transparency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creators.map((c) => (
                      <CreatorRow key={c.id} creator={c} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pagination && pagination.pages > 1 && (
              <nav className="mt-6 flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setValue('page', String(pagination.page - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-ink-muted">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setValue('page', String(pagination.page + 1))}
                >
                  Next
                </Button>
              </nav>
            )}
          </main>
        </div>
      </div>
    </>
  );
}

function CreatorRow({ creator: c }) {
  const cheapest = c.startingRates.length
    ? Math.min(...c.startingRates.map((r) => r.amountCents))
    : null;

  // Follower-weighted average engagement across platforms — a plain mean would
  // let a tiny high-engagement account distort a large one's headline number.
  const engagement = (() => {
    const rated = c.platforms.filter((p) => p.engagementRateBps != null && p.followers);
    if (!rated.length) return null;
    const followers = rated.reduce((s, p) => s + p.followers, 0);
    if (!followers) return null;
    return rated.reduce((s, p) => s + p.engagementRateBps * p.followers, 0) / followers;
  })();

  return (
    <tr>
      <td className="max-w-xs">
        <div className="flex items-center gap-2.5">
          {c.avatarUrl ? (
            <img src={c.avatarUrl} alt="" className="size-8 rounded-full object-cover" />
          ) : (
            <span className="flex size-8 items-center justify-center rounded-full bg-paper-sunken text-xs font-semibold text-ink-faint">
              {c.displayName.charAt(0)}
            </span>
          )}
          <span className="min-w-0">
            <Link to={`/creators/${c.slug}`} className="block font-medium text-ink hover:text-accent">
              {c.displayName}
            </Link>
            {c.headline && (
              <span className="block truncate text-xs text-ink-muted">{c.headline}</span>
            )}
          </span>
        </div>
      </td>
      <td>
        <div className="flex flex-wrap gap-1">
          {c.platforms.slice(0, 3).map((p) => (
            <Badge key={p.platform}>{PLATFORM_LABELS[p.platform] ?? p.platform}</Badge>
          ))}
          {c.platforms.length > 3 && <Badge>+{c.platforms.length - 3}</Badge>}
        </div>
      </td>
      <td className="num">{formatCount(c.totalFollowers)}</td>
      <td className="num">
        {engagement === null ? (
          <Unknown reason="Not reported" />
        ) : (
          formatPercent(bpsToPercent(engagement), { digits: 1 })
        )}
      </td>
      <td className="num">
        {cheapest === null ? <Unknown reason="No rates published yet" /> : formatCents(cheapest)}
      </td>
      <td className="num">
        <TransparencyBadge transparency={c.transparency} size="sm" showDetail={false} />
      </td>
    </tr>
  );
}
